import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { sanitizedPhoneNumber } from '@rsos/utils/phoneNumberUtils';
import { normalizeConversationOnCreate } from './normalizeConversation';
import { textFrom911Api, TWO_HOURS } from './textFrom911Api';

export const TF911_CONVERSATION_ENDED = 'CONVERSATION_ENDED';

const initialConversationState = {
  loading: {
    getConversationMetadata: false,
    createConversation: false,
  },
  error: {
    getConversationMetadata: false,
    createConversation: false,
  },
  data: {},
  participants: [],
  ui: {
    hasUnreadMessages: false,
    isTF911TransitionBannerDismissed: false,
  },
};

// Async thunk to initialize historic conversations and fetch metadata
export const initializeHistoricConversations = createAsyncThunk(
  'textFrom911/initializeHistoricConversations',
  async ({ orgName, removedCalls }, { dispatch }) => {
    const removedCallsHash = removedCalls
      ? removedCalls.reduce((hash, call) => {
          hash[call.caller_id] = true;
          return hash;
        }, {})
      : {};

    const historicResponse = await dispatch(
      textFrom911Api.endpoints.getHistoricConversations.initiate({
        orgName,
        startTime: new Date().getTime() - TWO_HOURS,
        status: 'active',
      }),
    );

    if (historicResponse.data) {
      const metadataPromises = historicResponse.data
        .map(conversation => {
          const callerID = sanitizedPhoneNumber(conversation.phone_number);
          if (removedCallsHash[callerID]) {
            return;
          }

          return dispatch(
            textFrom911Api.endpoints.getConversationMetadata.initiate({
              callerID,
              orgName,
              custom: { isOutboundSMS: true },
            }),
          );
        })
        .filter(Boolean);

      await Promise.all(metadataPromises);
    }

    return historicResponse.data;
  },
);

const initialState = {
  conversations: {},
  selectedConversation: null, // shouldn't be necessary. can map to selectedCallerID
  historicConversations: {},
  pendingTF911Message: null,
};

const textFrom911Slice = createSlice({
  name: 'textFrom911',
  initialState,
  reducers: {
    addTF911Conversation: (state, action) => {
      const callerID = action.payload.callerID;

      state.conversations[callerID] = {
        ...initialConversationState,
        data: action.payload.data || {},
      };
    },
    endConversation: (state, action) => {
      const callerID = action.payload;
      const existingConversation = state.conversations?.[callerID];
      // If the current user didn't end the conversation, there's a chance it
      // isn't in the store
      if (existingConversation) {
        return {
          ...state,
          conversations: {
            ...state.conversations,
            [callerID]: {
              ...existingConversation,
              data: {
                ...existingConversation.data,
                custom: {
                  ...existingConversation?.custom,
                  STATUS: TF911_CONVERSATION_ENDED,
                },
              },
            },
          },
        };
      }
      return state;
    },
    setSelectedConversation: (state, action) => {
      const callerID = action.payload;
      const existingConversation = state.conversations?.[callerID];
      if (existingConversation) {
        return {
          ...state,
          selectedConversation: callerID,
        };
      }
      return state;
    },
    setPendingTF911Message: (state, action) => {
      const message = action.payload;
      return {
        ...state,
        pendingTF911Message: message,
      };
    },
    dismissTF911TransitionBanner: (state, action) => {
      const callerID = action.payload.callerID;
      const existingConversation = state.conversations?.[callerID];
      if (existingConversation) {
        return {
          ...state,
          conversations: {
            ...state.conversations,
            [callerID]: {
              ...existingConversation,
              ui: {
                isTF911TransitionBannerDismissed: true,
              },
            },
          },
        };
      }
    },
    setConversationUnread: (state, action) => {
      const { callerID, hasUnreadMessages } = action.payload;
      const existingConversation = state.conversations?.[callerID];
      if (existingConversation) {
        return {
          ...state,
          conversations: {
            ...state.conversations,
            [callerID]: {
              ...existingConversation,
              ui: {
                ...existingConversation.ui,
                hasUnreadMessages: hasUnreadMessages,
              },
            },
          },
        };
      }
    },
    addParticipant: (state, action) => {
      const { callerID, participant } = action.payload;

      if (!callerID || !state.conversations[callerID]) {
        return;
      }

      const conversation = state.conversations[callerID];

      // Ensure participants array exists
      if (!conversation.participants) {
        conversation.participants = [];
      }

      if (conversation.participants.find(p => p.id === participant.id)) {
        return;
      }

      conversation.participants = [...conversation.participants, participant];
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        textFrom911Api.endpoints.getConversationMetadata.matchFulfilled,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerID = originalArgs.callerID;
          const metadata = action.payload;
          const existingConversation = state.conversations?.[callerID];

          // Don't create a new entry if there's no existing conversation
          // and the response is empty (e.g. 204 No Content). Creating a
          // phantom entry here causes useChatComponents to render a TF911
          // chat box alongside the TCAPI conversations chat box.
          if (!existingConversation && !metadata) {
            return state;
          }

          const isOutboundSMS = originalArgs?.custom?.isOutboundSMS;
          const data = {
            ...existingConversation?.data,
            ...metadata,
            ...(isOutboundSMS && isOutboundSMS),
          };
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data,
                ui: {
                  ...existingConversation?.ui,
                  // ternary is used to account for existing data so a migration isn't necessary. can be removed in future release
                  isTF911TransitionBannerDismissed:
                    isOutboundSMS &&
                    existingConversation?.ui?.isTF911TransitionBannerDismissed
                      ? !!existingConversation?.ui
                          ?.isTF911TransitionBannerDismissed
                      : false,
                },
                loading: {
                  ...existingConversation?.loading,
                  getConversationMetadata: false,
                },
                error: {
                  ...existingConversation?.error,
                  getConversationMetadata: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.getConversationMetadata.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];

          // Don't create a new entry just because a metadata query is
          // in-flight. Only update loading state for existing conversations.
          if (!existingConversation) {
            return state;
          }

          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  getConversationMetadata: true,
                },
                error: {
                  ...existingConversation?.error,
                  getConversationMetadata: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.getConversationMetadata.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  getConversationMetadata: false,
                },
                error: {
                  ...existingConversation?.error,
                  getConversationMetadata: action.error,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.createConversation.matchFulfilled,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          const normalizedPayload = normalizeConversationOnCreate(
            action.payload,
          );
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data: normalizedPayload,
                loading: {
                  ...existingConversation?.loading,
                  createConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  createConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.createConversation.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                participants: [],
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  createConversation: true,
                },
                error: {
                  ...existingConversation?.error,
                  createConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.createConversation.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  createConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  createConversation: action.error,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.endConversation.matchFulfilled,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data: {
                  ...existingConversation?.data,
                  custom: {
                    ...existingConversation?.data?.custom,
                    // STATUS capitalized to match backend
                    STATUS: TF911_CONVERSATION_ENDED,
                  },
                },
                loading: {
                  ...existingConversation?.loading,
                  endConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  endConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.endConversation.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  endConversation: true,
                },
                error: {
                  ...existingConversation?.error,
                  endConversation: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.endConversation.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  endConversation: false,
                },
                error: {
                  ...existingConversation?.error,
                  endConversation: action.error,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.getHistoricConversations.matchFulfilled,
        (state, action) => {
          return {
            ...state,
            historicConversations: action.payload,
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.createConversationOutboundSMS.matchFulfilled,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data: {
                  ...existingConversation?.data,
                  ...action.payload, // This should only be the conversation_id
                  isOutboundSMS: true, // isOutboundSMS gets overwritten with createConversation if
                  custom: {},
                  // adding an empty custom for new conversations
                  // prevents displaying start chat for conversations that should show end chat
                  // it occurs after
                },
                ui: {
                  isTF911TransitionBannerDismissed: false,
                },
                loading: {
                  ...existingConversation?.loading,
                  createConversationOutboundSMS: false,
                },
                error: {
                  ...existingConversation?.error,
                  createConversationOutboundSMS: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.createConversationOutboundSMS.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  createConversationOutboundSMS: true,
                },
                error: {
                  ...existingConversation?.error,
                  createConversationOutboundSMS: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.createConversationOutboundSMS.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  createConversationOutboundSMS: false,
                },
                error: {
                  ...existingConversation?.error,
                  createConversationOutboundSMS: {
                    ...action.error,
                    ...action.payload, // Includes the status
                  },
                },
              },
            },
          };
        },
      )
      // same as createConversationOutboundSMS but kept separate for phase 2 features
      .addMatcher(
        textFrom911Api.endpoints.createConversationOutboundAgero.matchFulfilled,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                data: {
                  ...existingConversation?.data,
                  ...action.payload, // This should only be the conversation_id
                  isOutboundSMS: true, // isOutboundSMS gets overwritten with createConversation if
                  custom: {},
                  // adding an empty custom for new conversations
                  // prevents displaying start chat for conversations that should show end chat
                  // it occurs after
                },
                ui: {
                  isTF911TransitionBannerDismissed: false,
                },
                loading: {
                  ...existingConversation?.loading,
                  createConversationOutboundAgero: false,
                },
                error: {
                  ...existingConversation?.error,
                  createConversationOutboundAgero: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.createConversationOutboundAgero.matchPending,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  createConversationOutboundAgero: true,
                },
                error: {
                  ...existingConversation?.error,
                  createConversationOutboundAgero: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        textFrom911Api.endpoints.createConversationOutboundAgero.matchRejected,
        (state, action) => {
          const callerID = action.meta.arg.originalArgs.callerID;
          const existingConversation = state.conversations?.[callerID];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                ...existingConversation,
                loading: {
                  ...existingConversation?.loading,
                  createConversationOutboundAgero: false,
                },
                error: {
                  ...existingConversation?.error,
                  createConversationOutboundAgero: {
                    ...action.error,
                    ...action.payload, // Includes the status
                  },
                },
              },
            },
          };
        },
      )
      .addMatcher(
        action => 'emergencyCalls/setOutboundSMSLocation'.includes(action.type),
        (state, action) => {
          const callerID = action.payload.caller_id;
          const existingConversation = state.conversations?.[callerID];
          const call_start_time = action?.payload?.call_start_time; // match snake case from `createConversationOutboundSMS`
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [callerID]: {
                data: {
                  ...existingConversation?.data,
                  call_start_time,
                  isOutboundSMS: true,
                  custom: {},
                },
                loading: {
                  ...existingConversation?.loading,
                  createConversationOutboundSMS: false,
                },
                error: {
                  ...existingConversation?.error,
                  createConversationOutboundSMS: null,
                },
              },
            },
          };
        },
      )
      .addMatcher(
        action => 'emergencyCalls/removeCall'.includes(action.type),
        (state, action) => {
          delete state.conversations[action.payload];
        },
      )
      .addMatcher(
        action => 'emergencyCalls/removeAllCalls'.includes(action.type),
        () => {
          return initialState;
        },
      )
      .addMatcher(
        action => ('LOGOUT_SUCCESS', 'SINATRA_LOGOUT').includes(action.type),
        () => {
          return initialState;
        },
      );
  },
});

const persistConfig = {
  key: 'textFrom911',
  storage,
};

const persistedReducer = persistReducer(
  persistConfig,
  textFrom911Slice.reducer,
);

export const selectTextFrom911Conversation = (state, callerId) => {
  return state.textFrom911.conversations[callerId];
};

export const selectConversationId = createSelector(
  [selectTextFrom911Conversation],
  text => {
    return text.data.conversation_id;
  },
);

export const {
  addTF911Conversation,
  endConversation,
  dismissTF911TransitionBanner,
  setConversationUnread,
  setSelectedConversation,
  setPendingTF911Message,
  addParticipant,
} = textFrom911Slice.actions;

export default persistedReducer;
